import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const theme = useTheme();
  // isMobile is used for responsive design decisions
  const _isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems: { key: 'home' | 'about' | 'menu' | 'gallery' | 'branches'; path: string }[] = [
    { key: 'home', path: '/#inicio' },
    { key: 'about', path: '/#nosotros' },
    { key: 'menu', path: '/menu' },
    { key: 'gallery', path: '/#galeria' },
    { key: 'branches', path: '/#sucursales' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleLanguage = () => {
    const current = (i18n.language || 'es').split('-')[0];
    const next = current === 'es' ? 'en' : 'es';
    console.log(`[Header] Language toggle: ${current} -> ${next}`);
    i18n.changeLanguage(next);
    console.log(`[Header] Language changed to: ${i18n.language}`);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const LanguageSwitcher = () => {
    const langCode = (i18n.language || 'es').split('-')[0];
    return (
      <Box
        onClick={toggleLanguage}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
          color: '#513628',
        }}
      >
        <img 
          src={`/images/flags/${langCode}.png`} 
          alt={langCode} 
          style={{ width: '25px', height: 'auto' }}
        />
        <Typography sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
          {langCode}
        </Typography>
        <ArrowDownIcon sx={{ width: '20px', height: '20px' }} />
      </Box>
    );
  };

  const MobileMenuIcon = () => (
    <svg width="30" height="20" viewBox="0 0 30 20" fill="none">
      <path 
        d="M0 0H30V3.33333H0V0ZM0 8.33333H17.5V11.6667H0V8.33333ZM0 16.6667H30V20H0V16.6667Z" 
        fill="#513628"
      />
    </svg>
  );

  const ClubEliteIcon = () => (
    <svg width="30" height="24" viewBox="0 0 27 24" fill="none">
      <path 
        d="M3 18L0 1.5L8.25 9L13.5 0L18.75 9L27 1.5L24 18H3ZM24 22.5C24 23.4 23.4 24 22.5 24H4.5C3.6 24 3 23.4 3 22.5V21H24V22.5Z" 
        fill="#F89C24"
      />
    </svg>
  );

  const drawer = (
    <Box sx={{ 
      backgroundColor: '#FFF0E5', 
      height: '100%', 
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background coffee elements */}
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        opacity: 0.05,
        zIndex: 0
      }}>
        <Box sx={{ 
          position: 'absolute', 
          top: '20px', 
          left: '20px'
        }}>
          <img 
            src="/images/left-coffee.svg" 
            alt="" 
            style={{ width: '200px' }}
          />
        </Box>
        <Box sx={{ 
          position: 'absolute', 
          top: '20px', 
          right: '20px'
        }}>
          <img 
            src="/images/right-coffee.svg" 
            alt="" 
            style={{ width: '500px' }}
          />
        </Box>
      </Box>

      {/* Close button */}
      <Box sx={{ 
        position: 'absolute', 
        top: '32px', 
        right: '24px', 
        zIndex: 2 
      }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon sx={{ 
            width: '40px', 
            height: '40px', 
            color: '#F89C24' 
          }} />
        </IconButton>
      </Box>

      {/* Mobile menu content */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1, 
        pt: 8, 
        px: 3, 
        pb: 3 
      }}>
        <List sx={{ mt: 8 }}>
          {navItems.map((item) => (
            <ListItem 
              key={item.key} 
              component={item.path.startsWith('/#') ? 'div' : RouterLink}
              to={!item.path.startsWith('/#') ? item.path : undefined}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                handleDrawerToggle();
                if (item.path.startsWith('/#')) {
                  e.preventDefault();
                  const sectionId = item.path.substring(2);
                  if (sectionId === 'inicio') {
                    if (location.pathname === '/') {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.location.href = '/';
                    }
                  } else {
                    if (location.pathname !== '/') {
                      window.location.href = '/' + (sectionId ? `#${sectionId}` : '');
                    } else {
                      const element = document.getElementById(sectionId);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }
                }
              }}
              sx={{
                justifyContent: 'center',
                py: 2,
                borderBottom: '1px solid rgba(81, 54, 40, 0.1)',
                cursor: 'pointer',
              }}
            >
              <ListItemText 
                primary={t(`layout.nav.${item.key}`)}
                sx={{
                  textAlign: 'center',
                  '& .MuiListItemText-primary': {
                    color: isActive(item.path) ? '#F89C24' : '#513628',
                    fontWeight: 'normal',
                    fontSize: '1.5rem',
                    fontFamily: '"Bebas Neue", sans-serif',
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{
          backgroundColor: isScrolled ? 'white' : '#FFF0E5',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
          boxShadow: isScrolled ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            px: { xs: 3, sm: 0, md: 0 }
          }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RouterLink to="/">
                <Box
                  component="img"
                  src="/images/logo.svg" 
                  alt="Tosto Coffee" 
                  sx={{
                    height: { xs: '55px', sm: '60px', md: '64px' }
                  }}
                />
              </RouterLink>
            </Box>

            {/* Mobile menu controls */}
            <Box sx={{ 
              display: { xs: 'flex', md: 'none' }, 
              alignItems: 'center', 
              gap: 3 
            }}>
              <LanguageSwitcher />
              <Box component="a" href="https://club.tostocoffee.com/login" target="_blank" rel="noopener noreferrer">
                <ClubEliteIcon />
              </Box>
              <IconButton onClick={handleDrawerToggle}>
                <MobileMenuIcon />
              </IconButton>
            </Box>

            {/* Desktop menu */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              alignItems: 'center',
              fontSize: '1.5rem',
              gap: 7
            }}>
              {/* Navigation items */}
              <Box component="ul" sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                listStyle: 'none',
                m: 0,
                p: 0,
                fontFamily: '"Bebas Neue", sans-serif',
                fontSize: '1.5rem',
              }}>
                {navItems.map((item) => (
                  <Box component="li" key={item.key}>
                    {item.path.startsWith('/#') ? (
                      <Box
                        component="a"
                        href={item.path}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          e.preventDefault();
                          const sectionId = item.path.substring(2); // Remove '/#'
                          if (sectionId === 'inicio') {
                            // Check if we're on the home page
                            if (location.pathname === '/') {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else {
                              // If not on home page, navigate to home page
                              window.location.href = '/';
                            }
                          } else {
                            // For other sections, check if we're on home page
                            if (location.pathname !== '/') {
                              window.location.href = '/' + (sectionId ? `#${sectionId}` : '');
                            } else {
                              const element = document.getElementById(sectionId);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth' });
                              }
                            }
                          }
                        }}
                        style={{
                          color: isActive(item.path) ? '#F89C24' : '#513628',
                          textDecoration: 'none',
                          fontWeight: 'normal',
                          fontSize: '1.5rem',
                          transition: 'color 0.3s ease',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#F89C24';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = isActive(item.path) ? '#F89C24' : '#513628';
                        }}
                      >
                        {t(`layout.nav.${item.key}`)}
                      </Box>
                    ) : (
                      <RouterLink
                        to={item.path}
                        style={{
                          color: isActive(item.path) ? '#F89C24' : '#513628',
                          textDecoration: 'none',
                          fontWeight: 'normal',
                          fontSize: '1.5rem',
                          transition: 'color 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#F89C24';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = isActive(item.path) ? '#F89C24' : '#513628';
                        }}
                      >
                        {t(`layout.nav.${item.key}`)}
                      </RouterLink>
                    )}
                  </Box>
                ))}
                
                {/* Club Elite button */}
                <Box component="li">
                  <Box component="a" href="https://club.tostocoffee.com/login" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none' }}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#F89C24',
                        color: 'white',
                        px: 4,
                        py: 0.5,
                        fontSize: '1.5rem',
                        fontWeight: 'normal',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontFamily: '"Bebas Neue", sans-serif',
                        '&:hover': {
                          backgroundColor: '#F89C24',
                          opacity: 0.9,
                        },
                      }}
                    >
                      {t('layout.nav.clubElite')}
                    </Button>
                  </Box>
                </Box>

                {/* Language switcher */}
                <Box component="li">
                  <LanguageSwitcher />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: '100%',
            backgroundColor: 'transparent',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 999,
          display: { xs: 'block', md: 'none' }
        }} onClick={handleDrawerToggle} />
      )}
    </>
  );
};

export default Header; 